import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

/**
 * SearchableCombobox — accessible, keyboard-driven, typeable combobox.
 * Props:
 *   - value: current selected option value (string | null)
 *   - onChange: (newValue: string) => void
 *   - options: Array<{ value: string, label: string, sublabel?: string }>
 *   - placeholder: shown when no value selected
 *   - searchPlaceholder: placeholder of the search input
 *   - emptyText: shown when no matches
 *   - disabled: disables the trigger
 *   - testId: data-testid for the trigger button
 *   - allowClear: if true, selecting the same value again clears it
 *   - keywords: optional (option) => string with extra hidden search text
 *     (e.g. an accent-stripped version so "pre" finds "PRÉ-MIRIM")
 */
export default function SearchableCombobox({
  value,
  onChange,
  options = [],
  placeholder = 'Selecione...',
  searchPlaceholder = 'Buscar...',
  emptyText = 'Nada encontrado',
  disabled = false,
  testId,
  allowClear = false,
  keywords,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'w-full justify-between bg-[#1A1A1A] border-[#27272A] text-white font-normal hover:bg-[#222]',
            !selected && 'text-[#71717A]',
            className,
          )}
        >
          <span className="truncate">{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[--radix-popover-trigger-width] p-0 bg-[#141414] border-[#27272A]"
      >
        <Command className="bg-transparent">
          <CommandInput placeholder={searchPlaceholder} className="text-white border-b border-[#27272A]" />
          <CommandList>
            <CommandEmpty className="py-4 text-center text-sm text-[#A1A1AA]">{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={`${opt.label} ${opt.sublabel || ''} ${typeof keywords === 'function' ? (keywords(opt) || '') : ''}`}
                  onSelect={() => {
                    onChange(allowClear && value === opt.value ? '' : opt.value);
                    setOpen(false);
                  }}
                  data-testid={`combobox-option-${opt.value}`}
                  className="text-white aria-selected:bg-[#27272A] cursor-pointer"
                >
                  <Check className={cn('mr-2 h-4 w-4', value === opt.value ? 'opacity-100' : 'opacity-0')} />
                  <div className="flex flex-col">
                    <span>{opt.label}</span>
                    {opt.sublabel && <span className="text-xs text-[#71717A]">{opt.sublabel}</span>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
